/**
 * New Auth Service - API-based authentication
 * Replaces direct Firebase/Firestore access with API calls
 */

import { auth } from '../sp-firebase/firebase';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signOut,
} from 'firebase/auth';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export interface SSOProvider {
  id: string;
  type: 'oidc' | 'saml';
  name: string;
  orgName: string;
  domains: string[];
  buttonStyle?: {
    bg?: string;
    text?: string;
  };
}

export const SSO_PROVIDERS: SSOProvider[] = [
  {
    id: 'saml.microsoftonline.com',
    type: 'saml',
    name: 'Strauss SSO',
    orgName: 'Strauss Group',
    domains: ['strauss-group.com', 'strauss.co.il'],
    buttonStyle: { bg: '#1a365d', text: 'white' }
  },
  {
    id: 'saml.saml-test',
    type: 'saml',
    name: 'Moonitor SSO',
    orgName: 'Moonitor',
    domains: ['moonitor.cloud'],
    buttonStyle: { bg: '#0078d4', text: 'white' }
  },
];

export function getSSOProviderForEmail(email: string): SSOProvider | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  return SSO_PROVIDERS.find(p => p.domains.includes(domain)) || null;
}

export interface LoginResult {
  success: boolean;
  error?: string;
  requiresSSO?: boolean;
}

/**
 * Login with email and password
 */
export async function loginWithEmailPassword(email: string, password: string): Promise<LoginResult> {
  const ssoProvider = getSSOProviderForEmail(email);
  if (ssoProvider) {
    return {
      success: false,
      error: `Your organization requires ${ssoProvider.name}. Please use the SSO option.`,
      requiresSSO: true,
    };
  }

  try {
    await signInWithEmailAndPassword(auth, email, password);
    // AuthBootstrap + onAuthStateChanged will handle backend verification and session
    return {
      success: true,
    };
  } catch (error: any) {
    console.error('Email/password login failed:', error);
    return {
      success: false,
      error: error.message || 'Login failed',
    };
  }
}

/**
 * Login with Google
 */
export async function loginWithGoogle(): Promise<LoginResult> {
  try {
    const provider = new GoogleAuthProvider();
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    if (isMobile) {
      await signInWithRedirect(auth, provider);
      return { success: true };
    } else {
      await signInWithPopup(auth, provider);
    }
    // Popup flow completed; AuthBootstrap will react to auth state change
    return { success: true };
  } catch (error: any) {
    console.error('Google login failed:', error);
    return {
      success: false,
      error: 'Failed to login with Google',
    };
  }
}

/**
 * Login with SSO
 */
export function loginWithSSO(provider: SSOProvider): void {
  console.log('[SSO] Starting redirect for:', provider.name, '| Provider ID:', provider.id, '| Type:', provider.type);
  console.log('[SSO] authDomain:', auth.config.authDomain);
  const ssoProvider = provider.type === 'saml'
    ? new SAMLAuthProvider(provider.id)
    : new OAuthProvider(provider.id);
  signInWithRedirect(auth, ssoProvider);
}

/**
 * Handle SSO redirect result — call this on app load to capture SSO errors.
 * Returns error message if SSO failed, null otherwise.
 */
export async function handleSSORedirectResult(): Promise<string | null> {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const providerId = result.providerId || 'unknown';
      console.log('[SSO] Redirect result:', {
        providerId,
        email: result.user?.email,
        uid: result.user?.uid,
      });
    }
    return null;
  } catch (error: any) {
    console.error('[SSO] Redirect error:', {
      code: error.code,
      message: error.message,
      email: error.customData?.email,
      credential: error.credential,
    });
    // Return user-friendly message based on error code
    const messages: Record<string, string> = {
      'auth/popup-closed-by-user': 'Login cancelled.',
      'auth/unauthorized-domain': `Domain not authorized for SSO. Contact support.`,
      'auth/invalid-credential': 'SSO configuration error. Contact support.',
      'auth/operation-not-allowed': 'SSO provider not enabled. Contact support.',
    };
    return messages[error.code] || `SSO login failed: ${error.message} (${error.code})`;
  }
}

/**
 * Send password reset email — routed through our backend + SendGrid mailer
 * (domain-authenticated spotlock.co sender) instead of Firebase's built-in
 * sender, for consistent branding and deliverability.
 */
export async function sendResetEmail(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_URL}/api/v2/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { success: false, error: error.error || 'Failed to send reset email' };
    }
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to send reset email' };
  }
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  sessionStorage.removeItem('uid');
  sessionStorage.removeItem('user');
  await signOut(auth);
}
